import React, { useEffect, useMemo, useState } from "react";
import apiService from "../services/api";
import {
  CUSTOMER_OWNED_REMOTE_NOTE,
  IANA_TIMEZONE_OPTIONS,
  INTENDED_DEVICE_CLASS,
  WEEKDAYS,
  WEEKDAY_LABELS,
  ZONE_NAME_PLACEHOLDER,
  applyDeviceTypeToAllZones,
  applyMondayToWeekdays,
  buildCreateBody,
  cloneHours,
  cloneZones,
  defaultOperatingHours,
  MA_DEVICE_INTENT_OPTIONS,
  deviceClassShortLabel,
  emptyLocationDraft,
  emptyZoneDraft,
  findBatchStoreDuplicates,
  hoursSummary,
  isCustomerOwnedClass,
  isZoneReady,
  validateLocationDraft,
  zonesSummary,
} from "../locationCreateHelpers";
import "../location-create.css";

function OperatingHoursEditor({ hours, onChange, disabled }) {
  const setDay = (day, patch) => {
    const next = cloneHours(hours);
    next[day] = { ...next[day], ...patch };
    if (patch.open === false) {
      next[day] = { open: false };
    }
    onChange(next);
  };

  return (
    <div>
      <table className="loc-hours-table">
        <thead>
          <tr>
            <th>Day</th>
            <th>Status</th>
            <th>Open</th>
            <th>Close</th>
          </tr>
        </thead>
        <tbody>
          {WEEKDAYS.map((day) => {
            const entry = hours?.[day] || { open: false };
            return (
              <tr key={day}>
                <td>{WEEKDAY_LABELS[day]}</td>
                <td>
                  <select
                    value={entry.open ? "open" : "closed"}
                    disabled={disabled}
                    onChange={(e) =>
                      setDay(
                        day,
                        e.target.value === "open"
                          ? {
                              open: true,
                              openTime: entry.openTime || "09:00",
                              closeTime: entry.closeTime || "21:00",
                            }
                          : { open: false },
                      )
                    }
                  >
                    <option value="open">Open</option>
                    <option value="closed">Closed</option>
                  </select>
                </td>
                <td>
                  {entry.open ? (
                    <input
                      type="time"
                      value={entry.openTime || "09:00"}
                      disabled={disabled}
                      onChange={(e) => setDay(day, { openTime: e.target.value })}
                    />
                  ) : (
                    "—"
                  )}
                </td>
                <td>
                  {entry.open ? (
                    <input
                      type="time"
                      value={entry.closeTime || "21:00"}
                      disabled={disabled}
                      onChange={(e) => setDay(day, { closeTime: e.target.value })}
                    />
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="loc-hours-actions">
        <button
          type="button"
          className="loc-create-secondary-btn"
          disabled={disabled}
          onClick={() => onChange(applyMondayToWeekdays(hours))}
        >
          Apply Monday to Weekdays
        </button>
      </div>
    </div>
  );
}

function ZonesPlaybackEditor({
  zones,
  onChange,
  disabled,
  showApplyZonesToAllLocations,
  onApplyZonesToAllLocations,
}) {
  const list = Array.isArray(zones) && zones.length ? zones : [emptyZoneDraft()];

  const updateZone = (clientZoneKey, patch) => {
    onChange(
      list.map((z) => (z.clientZoneKey === clientZoneKey ? { ...z, ...patch } : z)),
    );
  };

  const addZone = () => {
    onChange([...list, emptyZoneDraft({ expanded: true })]);
  };

  const removeZone = (clientZoneKey) => {
    if (list.length <= 1) return;
    onChange(list.filter((z) => z.clientZoneKey !== clientZoneKey));
  };

  const applyDeviceType = () => {
    const source =
      list.find((z) => z.expanded)?.intendedDeviceClass ||
      list[0]?.intendedDeviceClass ||
      INTENDED_DEVICE_CLASS.PLAYER_11;
    onChange(applyDeviceTypeToAllZones(list, source));
  };

  return (
    <div className="loc-zones">
      {list.map((zone, index) => {
        const ready = isZoneReady(zone);
        const customerOwned = isCustomerOwnedClass(zone.intendedDeviceClass);
        return (
          <div className="loc-zone-row" key={zone.clientZoneKey}>
            <div
              className="loc-zone-summary"
              onClick={() => updateZone(zone.clientZoneKey, { expanded: !zone.expanded })}
            >
              <span>{String(index + 1).padStart(2, "0")}</span>
              <span>{String(zone.name || "").trim() || "Zone name…"}</span>
              <span>{deviceClassShortLabel(zone.intendedDeviceClass)}</span>
              <span className={ready ? "loc-zone-ready" : "loc-zone-pending"}>
                {ready ? "Ready" : "Name needed"}
              </span>
              <span>{zone.expanded ? "▾" : "▸"}</span>
            </div>
            {zone.expanded && (
              <div className="loc-zone-expanded">
                <div className="loc-create-grid">
                  <div className="loc-create-field">
                    <label>Zone Name *</label>
                    <input
                      value={zone.name}
                      disabled={disabled}
                      placeholder={ZONE_NAME_PLACEHOLDER}
                      onChange={(e) =>
                        updateZone(zone.clientZoneKey, { name: e.target.value })
                      }
                    />
                  </div>
                  <div className="loc-create-field">
                    <label>Device Intent *</label>
                    <select
                      value={zone.intendedDeviceClass || INTENDED_DEVICE_CLASS.PLAYER_11}
                      disabled={disabled}
                      onChange={(e) => {
                        const nextClass = e.target.value;
                        updateZone(zone.clientZoneKey, {
                          intendedDeviceClass: nextClass,
                          intendedDeviceDescription: isCustomerOwnedClass(nextClass)
                            ? zone.intendedDeviceDescription || ""
                            : "",
                        });
                      }}
                    >
                      {MA_DEVICE_INTENT_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {customerOwned && (
                    <div className="loc-create-field full">
                      <label>Device Description (optional)</label>
                      <input
                        value={zone.intendedDeviceDescription || ""}
                        disabled={disabled}
                        placeholder="e.g. Samsung Galaxy Tab A9, model/notes"
                        onChange={(e) =>
                          updateZone(zone.clientZoneKey, {
                            intendedDeviceDescription: e.target.value,
                          })
                        }
                      />
                      <p className="loc-create-hint">{CUSTOMER_OWNED_REMOTE_NOTE}</p>
                    </div>
                  )}
                </div>
                <div className="loc-zone-actions">
                  <button
                    type="button"
                    className="loc-create-secondary-btn"
                    disabled={disabled || list.length <= 1}
                    onClick={() => removeZone(zone.clientZoneKey)}
                  >
                    Remove Zone
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
      <div className="loc-zones-actions">
        <button
          type="button"
          className="loc-create-secondary-btn"
          disabled={disabled}
          onClick={addZone}
        >
          + Add Zone
        </button>
        <button
          type="button"
          className="loc-create-secondary-btn"
          disabled={disabled || list.length < 2}
          onClick={applyDeviceType}
          title="Copies the expanded Zone's device type (or Zone 01) to every Zone"
        >
          Apply Device Type to All
        </button>
        {showApplyZonesToAllLocations && (
          <button
            type="button"
            className="loc-create-secondary-btn"
            disabled={disabled}
            onClick={() => onApplyZonesToAllLocations?.(list)}
          >
            Apply Zone Setup to All Locations
          </button>
        )}
      </div>
    </div>
  );
}

function LocationFields({
  row,
  onChange,
  disabled,
  showBatchActions,
  onApplyTimezoneAll,
  onApplyHoursAll,
  onApplyZonesAll,
}) {
  const set = (key, value) => onChange({ ...row, [key]: value });

  return (
    <>
      <div className="loc-create-grid">
        <div className="loc-create-field store-emphasis">
          <label>Store #</label>
          <input
            value={row.storeNumber}
            onChange={(e) => set("storeNumber", e.target.value)}
            disabled={disabled}
            placeholder="e.g. 1321"
          />
          <p className="loc-create-hint">Primary operational ID when used by Brand</p>
        </div>
        <div className="loc-create-field">
          <label>Location Label (optional)</label>
          <input
            value={row.label}
            onChange={(e) => set("label", e.target.value)}
            disabled={disabled}
            placeholder="e.g. Downtown, Airport Terminal B"
          />
        </div>
        <div className="loc-create-field">
          <label>Address Line 1 *</label>
          <input
            value={row.street}
            onChange={(e) => set("street", e.target.value)}
            disabled={disabled}
            required
          />
        </div>
        <div className="loc-create-field">
          <label>Address Line 2 / Suite / Unit</label>
          <input
            value={row.street2}
            onChange={(e) => set("street2", e.target.value)}
            disabled={disabled}
          />
        </div>
        <div className="loc-create-field">
          <label>City *</label>
          <input
            value={row.city}
            onChange={(e) => set("city", e.target.value)}
            disabled={disabled}
            required
          />
        </div>
        <div className="loc-create-field">
          <label>State / Province *</label>
          <input
            value={row.state}
            onChange={(e) => set("state", e.target.value)}
            disabled={disabled}
            required
          />
        </div>
        <div className="loc-create-field">
          <label>ZIP / Postal Code *</label>
          <input
            value={row.zipCode}
            onChange={(e) => set("zipCode", e.target.value)}
            disabled={disabled}
            required
          />
        </div>
        <div className="loc-create-field">
          <label>Country *</label>
          <input
            value={row.country}
            onChange={(e) => set("country", e.target.value)}
            disabled={disabled}
            required
          />
        </div>
        <div className="loc-create-field">
          <label>Timezone *</label>
          <select
            value={row.timezone}
            onChange={(e) => set("timezone", e.target.value)}
            disabled={disabled}
          >
            {IANA_TIMEZONE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {showBatchActions && (
            <button
              type="button"
              className="loc-create-secondary-btn"
              style={{ marginTop: 6, alignSelf: "flex-start" }}
              disabled={disabled}
              onClick={() => onApplyTimezoneAll?.(row.timezone)}
            >
              Apply timezone to all
            </button>
          )}
        </div>
      </div>

      <div className="loc-create-section" style={{ marginTop: 12, marginBottom: 0 }}>
        <div className="loc-create-section-header">
          <span>Operating Hours</span>
          <span style={{ fontWeight: 500, color: "#6c757d" }}>{hoursSummary(row.operatingHours)}</span>
        </div>
        <div className="loc-create-section-body">
          <OperatingHoursEditor
            hours={row.operatingHours || defaultOperatingHours()}
            disabled={disabled}
            onChange={(h) => set("operatingHours", h)}
          />
          {showBatchActions && (
            <div className="loc-hours-actions">
              <button
                type="button"
                className="loc-create-secondary-btn"
                disabled={disabled}
                onClick={() => onApplyHoursAll?.(row.operatingHours)}
              >
                Apply hours to all Locations
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="loc-create-section" style={{ marginTop: 12, marginBottom: 0 }}>
        <div className="loc-create-section-header">
          <span>Zones &amp; Playback</span>
          <span style={{ fontWeight: 500, color: "#6c757d" }}>{zonesSummary(row.zones)}</span>
        </div>
        <div className="loc-create-section-body">
          <ZonesPlaybackEditor
            zones={row.zones}
            disabled={disabled}
            showApplyZonesToAllLocations={showBatchActions}
            onApplyZonesToAllLocations={onApplyZonesAll}
            onChange={(nextZones) => set("zones", nextZones)}
          />
        </div>
      </div>
    </>
  );
}

/**
 * MA Brand-scoped Location create workspace.
 * Brand comes from Brand Manager context — never selectable here.
 */
export default function LocationCreateModal({
  open,
  onClose,
  onCreated,
  brandId = "",
  brandName = "",
}) {
  const [mode, setMode] = useState("single");
  const [rows, setRows] = useState([emptyLocationDraft()]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [globalWarning, setGlobalWarning] = useState(null);

  useEffect(() => {
    if (!open) return;
    setMode("single");
    setRows([emptyLocationDraft()]);
    setError(null);
    setGlobalWarning(null);
    setSubmitting(false);
  }, [open, brandId]);

  const summary = useMemo(() => {
    const total = rows.length;
    const created = rows.filter((r) => r.status === "created").length;
    const attention = rows.filter(
      (r) => r.status === "needs_attention" || r.status === "failed",
    ).length;
    const ready = total - created - attention;
    return { total, created, attention, ready };
  }, [rows]);

  if (!open) return null;

  const updateRow = (clientRowId, next) => {
    setRows((prev) => prev.map((r) => (r.clientRowId === clientRowId ? next : r)));
  };

  const addRow = () => {
    const template = rows[0] || emptyLocationDraft();
    setRows((prev) => [
      ...prev,
      emptyLocationDraft({
        timezone: template.timezone,
        country: template.country,
        operatingHours: cloneHours(template.operatingHours),
        zones: cloneZones(template.zones),
      }),
    ]);
  };

  const removeRow = (clientRowId) => {
    setRows((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((r) => r.clientRowId !== clientRowId);
    });
  };

  const applyTimezoneAll = (timezone) => {
    setRows((prev) =>
      prev.map((r) =>
        r.status === "created" ? r : { ...r, timezone, status: "ready", statusMessage: null },
      ),
    );
  };

  const applyHoursAll = (hours) => {
    const cloned = cloneHours(hours);
    setRows((prev) =>
      prev.map((r) =>
        r.status === "created"
          ? r
          : { ...r, operatingHours: cloneHours(cloned), status: "ready", statusMessage: null },
      ),
    );
  };

  const applyZonesAll = (zones) => {
    setRows((prev) =>
      prev.map((r) =>
        r.status === "created"
          ? r
          : {
              ...r,
              zones: cloneZones(zones),
              status: "ready",
              statusMessage: null,
            },
      ),
    );
  };

  const createOne = async (row, { confirmDuplicate = false } = {}) => {
    const missing = validateLocationDraft(row);
    if (missing.length) {
      return {
        ...row,
        status: "needs_attention",
        statusMessage: `Missing: ${missing.join(", ")}`,
      };
    }
    try {
      const body = buildCreateBody(row, { confirmDuplicateStoreNumber: confirmDuplicate });
      const raw = await apiService.createMyLocation(body, {
        idempotencyKey: row.idempotencyKey,
        activeBrandId: brandId,
      });
      const data = raw?.data || raw;
      return {
        ...row,
        status: "created",
        statusMessage: data?.idempotentReplay ? "Already created" : "Created",
        locationId: data?.locationId || data?.location?.locationId || null,
        confirmDuplicateStoreNumber: false,
      };
    } catch (err) {
      const payload = err?.payload || err?.body || null;
      const code = payload?.code || err?.code;
      if (code === "STORE_NUMBER_DUPLICATE_WARNING" || err?.status === 409) {
        return {
          ...row,
          status: "needs_attention",
          statusMessage:
            payload?.message ||
            "A Location with this Store # already exists for this Brand. Confirm to create anyway.",
          confirmDuplicateStoreNumber: true,
        };
      }
      return {
        ...row,
        status: "failed",
        statusMessage: err?.message || payload?.message || "Create failed",
      };
    }
  };

  const submit = async () => {
    if (!brandId) {
      setError("Active Brand context is required");
      return;
    }
    setSubmitting(true);
    setError(null);
    setGlobalWarning(null);

    const pending = rows.filter((r) => r.status !== "created");
    const batchDupes = findBatchStoreDuplicates(pending);
    if (batchDupes.size > 0) {
      setRows((prev) =>
        prev.map((r) =>
          batchDupes.has(r.clientRowId)
            ? {
                ...r,
                status: "needs_attention",
                statusMessage: "Duplicate Store # within this batch",
              }
            : r,
        ),
      );
      setGlobalWarning("Resolve duplicate Store # values within this batch before creating.");
      setSubmitting(false);
      return;
    }

    const nextRows = [...rows];
    let anyCreated = false;
    for (let i = 0; i < nextRows.length; i += 1) {
      const row = nextRows[i];
      if (row.status === "created") continue;
      const confirmDup = Boolean(row.confirmDuplicateStoreNumber);
      // eslint-disable-next-line no-await-in-loop
      const updated = await createOne(row, { confirmDuplicate: confirmDup });
      nextRows[i] = updated;
      setRows([...nextRows]);
      if (updated.status === "created") anyCreated = true;
    }

    setSubmitting(false);
    if (anyCreated) onCreated?.();
    const remaining = nextRows.filter((r) => r.status !== "created");
    if (remaining.length === 0) {
      onClose?.();
    }
  };

  const activeRows = mode === "single" ? [rows[0]] : rows;
  const brandLabel = String(brandName || "").trim();

  return (
    <div className="loc-create-overlay" data-testid="location-create-modal" onClick={onClose}>
      <div className="loc-create-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="loc-create-header">
          <h2>Add Location{mode === "multiple" ? "s" : ""}</h2>
          <button type="button" className="close-btn" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="loc-create-body">
          <div className="loc-create-mode" role="group" aria-label="Add mode">
            <button
              type="button"
              className={mode === "single" ? "active" : ""}
              onClick={() => {
                setMode("single");
                setRows((prev) => [prev[0] || emptyLocationDraft()]);
              }}
            >
              Single Location
            </button>
            <button
              type="button"
              className={mode === "multiple" ? "active" : ""}
              onClick={() => {
                setMode("multiple");
                setRows((prev) =>
                  prev.length >= 2
                    ? prev
                    : [
                        prev[0] || emptyLocationDraft(),
                        emptyLocationDraft({
                          timezone: (prev[0] || emptyLocationDraft()).timezone,
                          country: (prev[0] || emptyLocationDraft()).country,
                          operatingHours: cloneHours(
                            (prev[0] || emptyLocationDraft()).operatingHours,
                          ),
                          zones: cloneZones((prev[0] || emptyLocationDraft()).zones),
                        }),
                        emptyLocationDraft({
                          timezone: (prev[0] || emptyLocationDraft()).timezone,
                          country: (prev[0] || emptyLocationDraft()).country,
                          operatingHours: cloneHours(
                            (prev[0] || emptyLocationDraft()).operatingHours,
                          ),
                          zones: cloneZones((prev[0] || emptyLocationDraft()).zones),
                        }),
                      ],
                );
              }}
            >
              Multiple Locations
            </button>
          </div>

          {error && <div className="loc-create-error">{error}</div>}
          {globalWarning && <div className="loc-create-warn">{globalWarning}</div>}

          <div className="loc-create-section">
            <div className="loc-create-section-header">Location Context</div>
            <div className="loc-create-section-body">
              <p className="loc-create-context">
                Brand: <strong>{brandLabel || "—"}</strong>
                {!brandLabel && brandId ? (
                  <span className="loc-create-hint"> (Brand name unavailable)</span>
                ) : null}
              </p>
              <p className="loc-create-hint" style={{ marginTop: 4 }}>
                Brand owns each Location. This Master Account is assigned as Operator.
              </p>
            </div>
          </div>

          {mode === "multiple" && (
            <div className="loc-batch-summary">
              <span>
                <strong>{summary.total}</strong> Locations
              </span>
              <span>
                <strong>{summary.ready}</strong> Ready
              </span>
              <span>
                <strong>{summary.attention}</strong> Need attention
              </span>
              <span>
                <strong>{summary.created}</strong> Created
              </span>
              <button
                type="button"
                className="loc-create-secondary-btn"
                disabled={submitting}
                onClick={addRow}
              >
                + Add another Location
              </button>
            </div>
          )}

          {mode === "single" ? (
            <div className="loc-create-section">
              <div className="loc-create-section-header">Location Information</div>
              <div className="loc-create-section-body">
                <LocationFields
                  row={rows[0]}
                  disabled={submitting || rows[0]?.status === "created"}
                  onChange={(next) => updateRow(rows[0].clientRowId, { ...next, status: "ready", statusMessage: null })}
                />
                {rows[0]?.statusMessage && (
                  <div
                    className={
                      rows[0].status === "needs_attention" || rows[0].status === "failed"
                        ? "loc-create-warn"
                        : "loc-create-hint"
                    }
                    style={{ marginTop: 10 }}
                  >
                    {rows[0].statusMessage}
                    {rows[0].confirmDuplicateStoreNumber && (
                      <div style={{ marginTop: 6 }}>
                        <button
                          type="button"
                          className="loc-create-secondary-btn"
                          disabled={submitting}
                          onClick={() => submit()}
                        >
                          Create anyway
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            activeRows.map((row, index) => {
              const tzLabel =
                IANA_TIMEZONE_OPTIONS.find((o) => o.value === row.timezone)?.label ||
                row.timezone;
              return (
                <div className="loc-row" key={row.clientRowId}>
                  <div
                    className="loc-row-summary"
                    onClick={() =>
                      updateRow(row.clientRowId, { ...row, expanded: !row.expanded })
                    }
                  >
                    <span>{String(index + 1).padStart(2, "0")}</span>
                    <span>{row.storeNumber ? `#${row.storeNumber}` : "No Store #"}</span>
                    <span>{row.street || "Address…"}</span>
                    <span>
                      {[row.city, row.state].filter(Boolean).join(", ") || "City / State"}
                    </span>
                    <span>{tzLabel}</span>
                    <span>{zonesSummary(row.zones)}</span>
                    <span>{hoursSummary(row.operatingHours)}</span>
                    <span className={`loc-row-status ${row.status}`}>
                      {row.status === "needs_attention"
                        ? "Attention"
                        : row.status.charAt(0).toUpperCase() + row.status.slice(1)}
                    </span>
                    <span>{row.expanded ? "▾" : "▸"}</span>
                  </div>
                  {row.expanded && (
                    <div className="loc-row-expanded">
                      {row.statusMessage && (
                        <div
                          className={
                            row.status === "created" ? "loc-create-hint" : "loc-create-warn"
                          }
                        >
                          {row.statusMessage}
                        </div>
                      )}
                      <LocationFields
                        row={row}
                        disabled={submitting || row.status === "created"}
                        showBatchActions
                        onApplyTimezoneAll={applyTimezoneAll}
                        onApplyHoursAll={applyHoursAll}
                        onApplyZonesAll={applyZonesAll}
                        onChange={(next) =>
                          updateRow(row.clientRowId, {
                            ...next,
                            status: next.status === "created" ? "created" : "ready",
                            statusMessage:
                              next.status === "created" ? next.statusMessage : null,
                          })
                        }
                      />
                      <div className="loc-row-actions">
                        {row.confirmDuplicateStoreNumber && (
                          <button
                            type="button"
                            className="loc-create-secondary-btn"
                            disabled={submitting}
                            onClick={async () => {
                              setSubmitting(true);
                              const updated = await createOne(row, { confirmDuplicate: true });
                              updateRow(row.clientRowId, updated);
                              if (updated.status === "created") onCreated?.();
                              setSubmitting(false);
                            }}
                          >
                            Create this row anyway
                          </button>
                        )}
                        <button
                          type="button"
                          className="loc-create-secondary-btn"
                          disabled={submitting || rows.length <= 1 || row.status === "created"}
                          onClick={() => removeRow(row.clientRowId)}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        <div className="loc-create-footer">
          <button type="button" className="loc-create-ghost-btn" disabled={submitting} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="loc-create-primary-btn"
            disabled={submitting || !brandId}
            onClick={() => submit()}
          >
            {submitting
              ? "Creating…"
              : mode === "multiple"
                ? "Create Locations"
                : "Create Location"}
          </button>
        </div>
      </div>
    </div>
  );
}
